import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Boxes, Plus } from 'lucide-react';

import { lotsAPI } from '../../lib/lotsApi';
import { ETAPES_LOT, RETOURS_LOT, resteAConfirmer } from '../../lib/lotsFiltres';
import { formatMontant, formatPrixUnitaire } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { formatVolume } from '../../lib/utils';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { PageHeader } from '../../components/ui/page-header';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { StatutBadge } from '../../components/ui/StatutBadge';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { cn } from '../../lib/utils';
import LotDialog from '../../components/lots/LotDialog';
import { BarreEtapes, COULEURS_ETAPE, COULEURS_RETOUR, TuileCompteur } from '../../components/lots/lotsUi';
import { produitsDuLot } from '../../lib/lotsProduits';

const VUES = [
  ['', 'Tous'],
  ['OUVERT', 'Ouverts'],
  ['CLOS', 'Clos'],
];

/** Tableau de bord des lots attribués : compteurs par étape et par retour, avancement de chaque lot. */
const LotsDashboardPage = () => {
  const navigate = useNavigate();
  const { peutEcrire, voitArgent } = useDroitsLots();
  const [vue, setVue] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [dialogue, setDialogue] = useState(false);

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setChargement(true);
      try {
        setDonnees(await lotsAPI.dashboard({ status: vue }));
        setErreur(false);
      } catch (err) {
        console.error('Failed to load lots dashboard:', err);
        setErreur(true);
      } finally {
        setChargement(false);
      }
    },
    [vue],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['lots', 'trajets'], charger);

  const totaux = donnees?.totals;
  const lots = useMemo(() => donnees?.lots || [], [donnees]);

  // Un compteur ouvre le suivi sur ce filtre (et sur la même sélection de lots).
  const versSuivi = (filtre) => {
    const params = new URLSearchParams(filtre);
    if (vue) params.set('lot_status', vue);
    navigate(`/lots/suivi?${params.toString()}`);
  };

  if (chargement && !donnees) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="lots-dashboard">
      <PageHeader
        eyebrow="Lots attribués"
        title="Tableau de bord"
        stats={
          totaux
            ? [
                { label: 'Lots ouverts', valeur: donnees.open_lot_count, testid: 'lots-stat-ouverts' },
                { label: 'Camions attribués', valeur: totaux.attributed, testid: 'lots-stat-attribues' },
                { label: 'Confirmés', valeur: totaux.confirmed, ton: 'positif', testid: 'lots-stat-confirmes' },
                {
                  label: 'Reste à confirmer',
                  valeur: resteAConfirmer(totaux),
                  ton: resteAConfirmer(totaux) > 0 ? 'attention' : 'positif',
                  testid: 'lots-stat-reste',
                },
              ]
            : undefined
        }
        actions={
          peutEcrire ? (
            <Button onClick={() => setDialogue(true)} data-testid="lots-nouveau">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau lot
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label="Lots à compter">
          {VUES.map(([valeur, texte]) => (
            <button
              key={texte}
              type="button"
              onClick={() => setVue(valeur)}
              aria-pressed={vue === valeur}
              className={cn(
                'h-7 rounded-md px-2.5 text-xs font-semibold transition-colors',
                vue === valeur ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`lots-vue-${valeur || 'tous'}`}
            >
              {texte}
            </button>
          ))}
        </div>
        {donnees?.money?.by_currency?.length ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="lots-argent">
            {donnees.money.by_currency.map((ligne) => (
              <React.Fragment key={ligne.currency}>
                <span className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                  Valeur prévue{' '}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatMontant(ligne.expected_value, ligne.currency)}
                  </span>
                </span>
                <span className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                  Valeur chargée{' '}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatMontant(ligne.loaded_value, ligne.currency)}
                  </span>
                </span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      {erreur && !donnees ? (
        <EmptyState
          variant="error"
          title="Les lots n’ont pas pu être chargés"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={() => charger()}
        />
      ) : lots.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title={vue ? 'Aucun lot dans cette sélection' : 'Aucun lot attribué'}
          aide={
            vue ? undefined : 'Un lot rassemble les camions chargés vendus par un transporteur partenaire, à un prix au litre unique.'
          }
          actionLabel={peutEcrire && !vue ? 'Créer un lot' : undefined}
          onAction={peutEcrire && !vue ? () => setDialogue(true) : undefined}
        />
      ) : (
        <>
          {donnees?.money?.payments?.length ? (
            <section className="space-y-2" aria-label="Paiements" data-testid="lots-paiements-bloc">
              <h2 className="text-sm font-semibold text-foreground">Paiements</h2>
              <div className="flex flex-wrap gap-2">
                {donnees.money.payments.map((ligne) => (
                  <Link
                    key={ligne.currency}
                    to="/lots/paiements"
                    className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:border-primary/40 hover:bg-accent/50"
                    data-testid={`lots-paiements-${ligne.currency}`}
                  >
                    {[
                      ['Acomptes versés', ligne.advances_paid],
                      ['Décompté', ligne.settled_amount],
                      ['Reste dû', ligne.remaining_due],
                      ['Net dû', ligne.net_due],
                    ].map(([texte, valeur]) => (
                      <span key={texte} className="flex flex-col text-xs text-muted-foreground">
                        {texte}
                        <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">
                          {formatMontant(valeur, ligne.currency)}
                        </span>
                      </span>
                    ))}
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          <section className="space-y-2" aria-label="Camions par étape">
            <h2 className="text-sm font-semibold text-foreground">Par étape</h2>
            <div className="flex flex-wrap gap-2" data-testid="lots-tuiles-etapes">
              {ETAPES_LOT.filter((code) => code !== 'ANNULE' || Number(totaux?.stages?.[code]) > 0).map((code) => (
                <TuileCompteur
                  key={code}
                  testid={`lots-tuile-${code}`}
                  libelleTuile={libelle('lot_etape', code)}
                  valeur={Number(totaux?.stages?.[code]) || 0}
                  couleur={COULEURS_ETAPE[code]}
                  onClick={() => versSuivi({ stage: code })}
                />
              ))}
            </div>
          </section>

          <section className="space-y-2" aria-label="Retours des camions">
            <h2 className="text-sm font-semibold text-foreground">Retours</h2>
            <div className="flex flex-wrap gap-2" data-testid="lots-tuiles-retours">
              {RETOURS_LOT.map((code) => (
                <TuileCompteur
                  key={code}
                  testid={`lots-tuile-retour-${code}`}
                  libelleTuile={libelle('lot_retour', code)}
                  valeur={Number(totaux?.returns?.[code]) || 0}
                  couleur={COULEURS_RETOUR[code]}
                  onClick={() => versSuivi({ return_status: code })}
                />
              ))}
            </div>
          </section>

          <section className="space-y-2" aria-label="Avancement des lots">
            <h2 className="text-sm font-semibold text-foreground">Avancement des lots</h2>
            <Card className="card-base">
              <CardContent className="divide-y divide-border p-0">
                {lots.map((lot) => {
                  const avancement = lot.progress || {};
                  return (
                    <Link
                      key={lot.id}
                      to={`/lots/${lot.id}`}
                      className="grid gap-x-4 gap-y-2 px-4 py-3 transition-colors hover:bg-accent/50 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_minmax(0,16rem)] md:items-center"
                      data-testid={`lots-ligne-${lot.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-foreground">{lot.reference}</span>
                          <StatutBadge domaine="lot_statut" code={lot.status} />
                        </div>
                        <p className="truncate text-sm text-muted-foreground">
                          {lot.transporter_name || '—'}
                          {lot.products?.length || lot.product ? ` · ${produitsDuLot(lot)}` : ''}
                        </p>
                      </div>
                      <div className="min-w-0 space-y-1">
                        <BarreEtapes stages={avancement.stages} />
                        <p className="flex flex-wrap gap-x-3 text-xs tabular-nums text-muted-foreground">
                          <span>
                            <span className="font-semibold text-foreground">{avancement.attributed || 0}</span>
                            {lot.promised_trucks ? ` attribués sur ${lot.promised_trucks}` : ' attribués'}
                          </span>
                          <span>
                            <span className="font-semibold text-foreground">{avancement.confirmed || 0}</span> confirmés
                          </span>
                          <span>
                            <span className="font-semibold text-foreground">{resteAConfirmer(avancement)}</span> à confirmer
                          </span>
                        </p>
                      </div>
                      <div className="text-xs tabular-nums text-muted-foreground md:text-right">
                        <p>
                          Prévu {formatVolume(avancement.expected_liters)} · chargé {formatVolume(avancement.loaded_liters)}
                        </p>
                        {lot.money ? (
                          <p data-testid={`lots-valeur-${lot.id}`}>
                            {formatMontant(lot.money.loaded_value, lot.money.currency)}
                            {lot.money.price_per_liter !== null && lot.money.price_per_liter !== undefined
                              ? ` · ${formatPrixUnitaire(lot.money.price_per_liter, lot.money.currency)}/L`
                              : ''}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  );
                })}
              </CardContent>
            </Card>
          </section>
        </>
      )}

      {erreur && donnees ? (
        <p className="text-xs text-destructive" role="alert">
          Les chiffres n’ont pas pu être actualisés.
        </p>
      ) : null}

      {peutEcrire ? (
        <LotDialog open={dialogue} onOpenChange={setDialogue} voitArgent={voitArgent} onSaved={() => charger({ silencieux: true })} />
      ) : null}
    </div>
  );
};

export default LotsDashboardPage;
